// Constants
import { UserRole, UserStatus } from '@shared/constants';

// Types
import {
  Project,
  ProjectActivity,
  ProjectInfo,
  ProjectMember,
} from '@shared/types';
import type { RelationshipUser } from '@/types/employeeRelationship';
import {
  EmployeeTraining,
  EmployeeActivity,
  EmployeeTrainingActivity,
  EmployeeEducation,
} from './employee';

export enum HttpMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export type MetaData = {
  pagination: {
    limit: number;
    cursor?: string;
  };
};

export type ApiResponse<T> = {
  data: T | null;
  error: string | null;
  status: number;
};

export type Resources = {
  resumeId: string;
};

export type UserProfile = {
  id?: string;
  email?: string;
  name?: string;
  status?: UserStatus;
  role?: UserRole;
  lastSignIn?: string;
  room?: string;
  avatar?: string;
  authId?: string;
  username?: string;
  resources?: Resources;
};

export type UserProfileSummary = Pick<UserProfile, 'id' | 'status' | 'role'>;

export type StatisticsType = {
  totalUsers: number;
  totalResumes: number;
  lastSignIn: string;
  lastResumeUpdateAt: string;
};

export interface UserListResponse {
  data: UserProfile[];
  meta: MetaData;
}

export interface ProjectListResponse {
  data: Project['projectInfo'][];
  meta: MetaData;
}

export interface MemberRemovePayloadSocket {
  project: string;
  user: string;
}

export interface MemberAddPayloadSocket extends ProjectInfo {
  members: ProjectMember[];
}

export interface MemberProjectActivitySocket {
  activities: ProjectActivity[];
}

type RelationshipResponseBase = {
  id: string;
  name: string;
  username: string;
  avatar?: string;
};

export type AssignMentorResponse = {
  data: RelationshipResponseBase & {
    mentor: RelationshipUser;
  };
};

export type UnassignMentorResponse = {
  data: RelationshipResponseBase & {
    unAssignedMentor: RelationshipUser;
  };
};

export type AssignMenteeResponse = {
  data: RelationshipResponseBase & {
    mentees: RelationshipUser[];
  };
};

export type UnassignMenteeResponse = {
  data: RelationshipResponseBase & {
    unAssignedMentees: RelationshipUser[];
  };
};

export type EmployeeProjectInfoResponse = {
  member?: {
    id?: string;
    project?: string;
    status?: string;
    tag?: string;
    role?: string;
    user?: string;
  };
};

export interface EmployeeTrainingResponse {
  data: EmployeeTraining[];
  meta: MetaData;
}

export type TrainingPresentationResponse = {
  data: Omit<EmployeeActivity, 'data'> & {
    data: {
      presentationAt?: string;
      training?: string;
      presentationContent?: string;
      user?: string;
      presentationTitle?: string;
      topicLabel?: string;
    };
  };
};

export type TrainingFeedbackResponse = {
  data: Omit<EmployeeActivity, 'data'> & {
    data: Partial<EmployeeTrainingActivity> & {
      topicLabel?: string;
    };
  };
};

export type EmployeeEducationResponse = {
  education?: EmployeeEducation[];
};

export type ProjectInfoResponse = Partial<
  Pick<ProjectInfo, 'id' | 'code' | 'name' | 'desc' | 'logo'>
>;

export type ProjectMembersResponse = Partial<ProjectMember>[];
